import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { RevealLine, Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import {
  // Testimonials,
  // StatsSection,
  Ribbon, TrustBar, ServicesGrid, IndustriesGrid, ProcessTimeline,
  WhySynani, CtaSection, SectionHeading, Kicker,
} from "../components/Sections";
import { EmployerForm, CandidateForm } from "../components/Forms";
import { ABOUT_PILLARS } from "../lib/data";
import { BRAND } from "../lib/site";

const Hero = () => (
  <section className="relative overflow-hidden bg-ice pt-28 md:pt-36 pb-16 md:pb-24" data-testid="hero-section">
    <div className="absolute right-0 top-0 h-full w-full md:w-1/2 pointer-events-none" aria-hidden="true">
      <img
        src="https://images.unsplash.com/photo-1644325349124-d1756b79dd42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNvbm5lY3RpbmclMjBsaW5lcyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzg3MDcwNDg1fDA&ixlib=rb-4.1.0&q=85"
        alt=""
        className="h-full w-full object-cover opacity-[0.14] md:opacity-30 mix-blend-multiply"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-ice via-ice/60 to-transparent" />
    </div>
    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
        <Kicker>IT &amp; Non-IT Talent Solutions</Kicker>
      </motion.div>
      <h1 className="mt-6 font-heading font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] text-navy max-w-4xl">
        <RevealLine delay={0.15}>Talent That Moves</RevealLine>
        <RevealLine delay={0.3}>Businesses <span className="text-sapphire">Forward</span></RevealLine>
      </h1>
      <motion.p
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
        className="mt-7 max-w-xl text-lg text-ink leading-relaxed">
        We help organizations build high-performing teams by connecting them with the right talent across IT and Non-IT industries.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
        className="mt-9 flex flex-col sm:flex-row gap-3">
        <Link to="/employers" data-testid="hero-find-talent"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-sapphire hover:bg-sapphire-dark text-white font-semibold transition-colors">
          Find the Right Talent <ArrowUpRight size={18} aria-hidden="true" />
        </Link>
        <Link to="/careers" data-testid="hero-explore-jobs"
          className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-slate-300 text-navy font-semibold hover:border-navy transition-colors">
          Explore Opportunities
        </Link>
      </motion.div>
    </div>
  </section>
);

const About = () => (
  <section id="about" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 md:py-28" data-testid="about-section">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
      <Reveal>
        <div className="relative">
          <div className="absolute -left-4 -top-4 w-24 h-24 border-t-2 border-l-2 border-sapphire" aria-hidden="true" />
          <img
            src="https://images.pexels.com/photos/7108269/pexels-photo-7108269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="SYNANI Talent Solutions recruiters reviewing candidate profiles together"
            width="940"
            height="650"
            loading="lazy"
            className="w-full h-[420px] object-cover rounded-xl"
          />
          <div className="absolute -right-4 -bottom-4 w-24 h-24 border-b-2 border-r-2 border-navy" aria-hidden="true" />
        </div>
      </Reveal>
      <div>
        <Reveal><SectionHeading kicker={`About ${BRAND.name}`} title="A modern talent partner for a changing workforce" /></Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-ink leading-relaxed text-lg">
            SYNANI is a talent solutions and staffing company helping organizations find and engage skilled professionals across IT and Non-IT domains.
          </p>
          <p className="mt-4 text-ink leading-relaxed text-lg">
            We combine industry understanding, human expertise, structured recruitment and technology-enabled sourcing to help businesses build the teams they need to grow.
          </p>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-6">
          {ABOUT_PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={0.15 + i * 0.06}>
              <div>
                <h3 className="font-bold text-navy">{p.title}</h3>
                <p className="mt-1.5 text-sm text-ink leading-relaxed">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ConnectForms = () => {
  const [tab, setTab] = useState("employer");
  return (
    <section className="bg-white border-y border-slate-200 py-20 md:py-28" data-testid="forms-section">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center">
            <Kicker>Get Started</Kicker>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-navy">
              {tab === "employer" ? "Need the Right Talent?" : "Your Next Opportunity Starts Here"}
            </h2>
            <p className="mt-4 max-w-xl mx-auto text-ink">
              {tab === "employer"
                ? "Tell us what you're looking for. Our recruitment specialists will help you identify, evaluate and connect with the right professionals."
                : "Explore career opportunities and connect with employers looking for your skills and experience."}
            </p>
          </div>
        </Reveal>
        <div className="mt-8 flex justify-center">
          <div className="inline-flex p-1 rounded-full border border-slate-200 bg-ice" role="tablist" aria-label="Enquiry type">
            {[["employer", "For Employers"], ["candidate", "For Candidates"]].map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-navy text-white" : "text-navy"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-10">
          {tab === "employer" ? <EmployerForm /> : <CandidateForm />}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Seo
        description="SYNANI Talent Solutions is an IT & Non-IT recruitment and staffing company in Chennai, India. Contract and permanent staffing, RPO services and executive search — talent that moves businesses forward."
        keywords="IT Recruitment Company, IT Staffing Services, Non-IT Recruitment, Recruitment Consultancy, Staffing Solutions, Talent Acquisition, Contract Staffing, Permanent Staffing, RPO Services, Recruitment Company in Chennai"
      />
      <Hero />
      <Ribbon />
      <TrustBar />
      <About />
      <ServicesGrid />
      <IndustriesGrid />
      <ProcessTimeline />
      <WhySynani />
      {/* <StatsSection /> */}
      {/* <Testimonials /> */}
      <ConnectForms />
      <CtaSection />
    </>
  );
}
